<template>
  <div class="container-fluid mt-5">
    <div>
      <card
        class="no-border-card"
        body-classes="px-0 pb-1"
        footer-classes="pb-2"
      >
        <template slot="header">
          <div class="row">
            <div class="col-8">
              <h3 class="mb-0 view-header-buttons">
                <span class="text-before-button">{{ reseller.name }}</span>
                <base-button
                  v-if="$currentUserCan($permissions.PERM_EDIT_RESELLERS)"
                  type="primary"
                  icon
                  size="sm"
                  @click="editReseller()"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-edit"></i>
                  </span>
                </base-button>

                <base-button
                  v-if="$currentUserCan($permissions.PERM_DELETE_RESELLERS)"
                  type="danger"
                  icon
                  size="sm"
                  @click="deleteReseller()"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-trash"></i>
                  </span>
                </base-button>
              </h3>
            </div>
            <div class="col-4 text-right">
              <base-button
                @click="goBack()"
                type="button"
                class="btn btn-sm btn-primary"
              >
                {{ $t("COMMON.RETURN_TO_LIST") }}
              </base-button>

              <notification-subscription
                :objectType="'resellers'"
                :objectId="reseller.id"
                :events="{
                  UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                  DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
                }"
              />
            </div>
          </div>
        </template>
        <div>
          <div
            class="col-12 justify-content-center justify-content-sm-between flex-wrap tabs-wrapper"
          >
            <tabs
              fill
              class="flex-column flex-md-row"
              tabNavWrapperClasses="nav-wrapper"
              tabNavClasses="nav nav-pills nav-fill"
              value="global"
            >
              <card shadow>
                <tab-pane title="global" id="1" :active="true">
                  <span slot="title">
                    <i class="ni ni-cloud-upload-96" />
                    {{ $t("COMMON.GLOBAL") }}
                  </span>
                  <reseller-view-global :reseller="reseller" />
                </tab-pane>

                <tab-pane title="organizations" id="2">
                  <span slot="title">
                    <i class="ni ni-building" />
                    {{ $t("COMMON.ORGANIZATIONS") }}
                  </span>
                  <reseller-view-organizations :reseller="reseller" />
                </tab-pane>

                <tab-pane title="users" id="3">
                  <span slot="title">
                    <i class="ni ni-single-02" />
                    {{ $t("COMMON.USERS") }}
                  </span>
                  <reseller-view-users :reseller="reseller" />
                </tab-pane>

                <tab-pane title="roles" id="4">
                  <span slot="title">
                    <i class="ni ni-hat-3" />
                    {{ $t("COMMON.ROLES") }}
                  </span>
                  <reseller-view-roles :reseller="reseller" />
                </tab-pane>

                <tab-pane
                  title="logs"
                  id="999"
                  v-if="$currentUserCan($permissions.PERM_VIEW_ANY_LOGS)"
                >
                  <span slot="title">
                    <i class="fa fa-file" />
                    {{ $t("COMMON.LOGS") }}
                  </span>
                  <reseller-view-logs :reseller="reseller" />
                </tab-pane>
              </card>
            </tabs>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import {
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import swal from "sweetalert2";
import { cloneDeep } from "lodash";
import { Tabs, TabPane } from "@/components";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import defaultReseller from "./defaultReseller";
import ResellerViewGlobal from "./partials/ResellerViewGlobal.vue";
import ResellerViewOrganizations from "./partials/ResellerViewOrganizations.vue";
import ResellerViewUsers from "./partials/ResellerViewUsers.vue";
import ResellerViewRoles from "./partials/ResellerViewRoles.vue";
import ResellerViewLogs from "./partials/ResellerViewLogs.vue";

export default {
  layout: "DashboardLayout",

  components: {
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
    Tabs,
    TabPane,
    ResellerViewGlobal,
    ResellerViewOrganizations,
    ResellerViewUsers,
    ResellerViewRoles,
    ResellerViewLogs,
    NotificationSubscription,
  },

  mixins: [],

  data() {
    return {
      reseller: cloneDeep(defaultReseller),
    };
  },

  computed: {},

  watch: {},

  created() {
    this.get();
  },

  methods: {
    async get() {
      try {
        const id = this.$route.params.id;
        await this.$store.dispatch("resellers/get", id);
        this.reseller = this.$store.getters["resellers/reseller"];
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async editReseller(row) {
      this.$router.push({
        name: "Edit Reseller",
        params: { id: this.reseller.id },
      });
    },

    async deleteReseller(id) {
      const confirmation = await swal.fire({
        title: this.$t("RESELLERS.DELETE_THIS_RESELLER"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch("resellers/destroy", id);
          await this.getList();
          this.$notify({
            type: "success",
            message: this.$t("RESELLERS.RESELLER_DELETED"),
          });
        }
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    goBack() {
      this.$router.push({ name: "List Resellers" });
    },
  },
};
</script>
